import {
  useV2CreateRateTableMutation,
  useV2UpdateRateTableMutation,
  useV2DeleteRateTableMutation,
} from "@generated";
import type { V2RateTableResource } from "@generated";
import type { RateTableFormValues } from "../types";

// Build the JSON:API `data` body. The `account` relationship is inferred
// server-side and omitted; `accounting-code` is serialized by the API but is
// absent from the generated type, so we cast (kit convention).
export const buildRateTableData = (
  values: RateTableFormValues,
  id?: string
): V2RateTableResource => {
  const data: any = {
    type: "rate-tables",
    ...(id ? { id } : {}),
    attributes: {
      name: values.name,
      default: values.default,
      "accounting-code": values.accountingCode,
    },
  };
  if (values.currencyId) {
    data.relationships = {
      currency: { data: { type: "currencies", id: values.currencyId } },
    };
  }
  return data as unknown as V2RateTableResource;
};

export const useRateTableMutations = () => {
  const [create, createState] = useV2CreateRateTableMutation();
  const [update, updateState] = useV2UpdateRateTableMutation();
  const [destroy, destroyState] = useV2DeleteRateTableMutation();

  return {
    create: (values: RateTableFormValues) =>
      create({ body: { data: buildRateTableData(values) } }).unwrap(),

    update: (id: string, values: RateTableFormValues) =>
      update({
        id: id,
        body: { data: buildRateTableData(values, id) },
      }).unwrap(),

    // Archive (soft-delete: API stamps deleted_at; restore re-activates).
    archive: (id: string) => destroy({ id: id }).unwrap(),

    restore: (id: string, name: string) =>
      update({
        id: id,
        body: {
          data: {
            type: "rate-tables",
            id: id,
            attributes: { name, active: true },
          } as unknown as V2RateTableResource,
        },
      }).unwrap(),

    states: { createState, updateState, destroyState },
  };
};

export default useRateTableMutations;
